import React, { useCallback, useMemo, useState } from 'react';
import FormEdit from '../../../../core/components/form/FormEdit';
import FormGroup from '../../../../core/components/form/FormGroup';
import ValidInputRadioText from '../../../../core/components/validation/ValidInputRadioText';
import { useProviderEditModel } from './useProviderEditModel';
import ValidInputText from '../../../../core/components/validation/ValidInputText';
import ValidInputDroplistText from '../../../../core/components/validation/ValidInputDroplistText';
import Button from '../../../../core/components/Button';
import Dialog from '../../../../core/components/Dialog';

const wideField = 240;
const validationMessage = 'Confirm you wish to Validate the provider. Once saved this cannot be changed.';
const faxConsentMessage = (val: boolean): string =>
    `Confirm the provider has ${val ? '' : 'not '}consented to fax requests.`;

const ProviderEditAccountTab = (): JSX.Element => {
    const { model, submit, setPropText, cancelEdit, waiting } = useProviderEditModel();
    const [open, setOpen] = useState(false);
    const [currentDialog, setCurrentDialog] = useState<{ title: string; message: string }>({ title: '', message: '' });

    const dialogActions = useMemo(
        () => [
            {
                label: 'OK',
                onClick: (): void => {
                    setOpen(false);
                    if (currentDialog.title === 'Validate Provider') {
                        setPropText(model.Validated.key, 'true');
                    } else if (currentDialog.title === 'Fax Consent') {
                        setPropText(model.FaxConsent.key, model.FaxConsent.val === 'true' ? 'false' : 'true');
                    }
                },
            },
            {
                label: 'CANCEL',
                onClick: (): void => setOpen(false),
                cssName: 'btn-cancel',
            },
        ],
        [currentDialog.title, model.FaxConsent.key, model.FaxConsent.val, model.Validated.key, setPropText],
    );

    const openValidationDialog = useCallback((): void => {
        setCurrentDialog({ title: 'Validate Provider', message: validationMessage });
        setOpen(true);
    }, []);

    const openFaxConsentDialog = useCallback((): void => {
        setCurrentDialog({
            title: 'Fax Consent',
            message: faxConsentMessage(model.FaxConsent.val === 'true' ? false : true),
        });
        setOpen(true);
    }, [model.FaxConsent.val]);

    return (
        <FormEdit formName="providerEdit">
            <Dialog
                open={open}
                setOpen={setOpen}
                title={currentDialog.title}
                message={currentDialog.message}
                actions={dialogActions}
            />
            <FormGroup>
                <ValidInputRadioText
                    prop={model.Validated}
                    onChange={openValidationDialog}
                    disabled={waiting || model.Validated.val === 'true'}
                />
            </FormGroup>

            <FormGroup title="Account">
                <ValidInputText
                    prop={model.NPI}
                    placeholder={model.NPI.lbl}
                    onChange={setPropText}
                    disabled
                />
                <ValidInputText
                    prop={model.FullName}
                    placeholder="Full Name"
                    onChange={setPropText}
                    width={wideField}
                    disabled
                />
                <ValidInputText
                    prop={model.PracticeName}
                    placeholder={model.PracticeName.lbl}
                    onChange={setPropText}
                    width={wideField}
                    disabled={waiting}
                />
                <ValidInputDroplistText
                    prop={model.PrimaryTaxonomy}
                    onChange={setPropText}
                    disabled={waiting}
                />
            </FormGroup>

            <FormGroup title="Address / Phone">
                <ValidInputText
                    prop={model.Address1}
                    alternateLabel="Line 1"
                    placeholder="Address Line 1"
                    onChange={setPropText}
                    width={wideField}
                    disabled={waiting}
                />
                <ValidInputText
                    prop={model.Address2}
                    alternateLabel="Line 2"
                    placeholder="Address Line 2"
                    onChange={setPropText}
                    width={wideField}
                    disabled={waiting}
                />
                <ValidInputText
                    prop={model.City}
                    placeholder={model.City.lbl}
                    onChange={setPropText}
                    width={wideField}
                    disabled={waiting}
                />
                <ValidInputDroplistText
                    prop={model.State}
                    onChange={setPropText}
                    disabled={waiting}
                />
                <ValidInputText
                    prop={model.Zip}
                    placeholder="12345-1234"
                    onChange={setPropText}
                    type="Zip"
                    disabled={waiting}
                />
                <ValidInputText
                    prop={model.Phone}
                    placeholder="123-456-7890"
                    onChange={setPropText}
                    type="Phone/Fax"
                    disabled={waiting}
                />
            </FormGroup>

            <FormGroup title="Fax Request">
                <ValidInputText
                    prop={model.Fax}
                    placeholder="123-456-7890"
                    onChange={setPropText}
                    alternateLabel="Fax Number"
                    type="Phone/Fax"
                    disabled={waiting}
                />
                <ValidInputRadioText
                    prop={model.FaxConsent}
                    onChange={openFaxConsentDialog}
                    alternateLabel="Accepts Fax"
                    disabled={waiting}
                />
                {model.FaxConsent.val === 'true' ? (
                    <>
                        <ValidInputDroplistText
                            prop={model.FaxFrequency}
                            onChange={setPropText}
                            interpretAs="number"
                            disabled={waiting}
                        />
                        <ValidInputDroplistText
                            prop={model.FaxAttempts}
                            onChange={setPropText}
                            interpretAs="number"
                            disabled={waiting}
                        />
                    </>
                ) : null}
            </FormGroup>

            <div className="provider-edit-form-btns">
                <Button
                    cssName="btn-submit"
                    onClick={submit}
                    disabled={waiting}
                >
                    UPDATE
                </Button>
                <Button
                    cssName="btn-cancel"
                    onClick={cancelEdit}
                    disabled={waiting}
                >
                    CANCEL
                </Button>
            </div>
        </FormEdit>
    );
};

export default ProviderEditAccountTab;
